import React, { useEffect, useState, useMemo, useRef } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from '@material-ui/core';
import axios from 'axios';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import yaml from 'js-yaml';
import nunjucks from 'nunjucks';

// Configure nunjucks with your desired delimiters.
nunjucks.configure({
  autoescape: false,
  tags: {
    variableStart: '${{',
    variableEnd: '}}',
  },
});

export const SelectAdoProduct = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, formData, required, rawErrors, schema, uiSchema, formContext } = props;

  // Get the adoProject template from ui:options and render it using the entire form context.
  const adoProjectTemplate = uiSchema['ui:options']?.adoProject || '';

  const resolvedAdoProject = useMemo(() => {
    if (!adoProjectTemplate) return '';
    return nunjucks.renderString(adoProjectTemplate, formContext.formData || {}).trim();
  }, [adoProjectTemplate, formContext.formData]);

  console.log('SelectAdoProduct: resolved adoProject =', resolvedAdoProject);
  console.log('SelectAdoProduct: formContext.formData =', formContext.formData);

  const endpoint = `/backstage/project/${resolvedAdoProject}`;

  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  // Use separate states for fetch errors and validation (required field) errors.
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const prevAdoProjectRef = useRef(resolvedAdoProject);

  useEffect(() => {
    // When adoProject changes, clear the current product value.
    if (prevAdoProjectRef.current !== resolvedAdoProject) {
      onChange('');
      prevAdoProjectRef.current = resolvedAdoProject;
    }

    // Reset options and fetch error.
    setOptions([]);
    setFetchError(null);

    // If no project is selected, skip fetching and set a fetch error.
    if (!resolvedAdoProject) {
      setLoading(false);
      setFetchError('Please select a valid ADO Project');
      return;
    }

    setLoading(true);
    async function fetchProducts() {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const credentials = await identityApi.getCredentials();
        const token = credentials.token;
        const response = await axios.post(
          `${baseUrl}/aws/params`,
          { name: endpoint },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const rawValue = response.data.value || '';
        if (!rawValue) throw new Error('Invalid response: No value returned');

        const parsed = yaml.load(rawValue);
        let productList: string[] = [];
        if (parsed && typeof parsed === 'object' && Array.isArray((parsed as any).products)) {
          productList = (parsed as any).products;
        } else if (typeof parsed === 'string') {
          productList = parsed.split(',').map((p: string) => p.trim()).filter((p: string) => p);
        } else {
          throw new Error('Products list not found in response');
        }

        setOptions(productList);
        setFetchError(null);
      } catch (err: any) {
        console.error('Error fetching ADO products:', err);
        setFetchError('Failed to fetch products. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchProducts();
  }, [resolvedAdoProject, endpoint, identityApi, discoveryApi, onChange]);

  // Separate effect to enforce required-field validation.
  useEffect(() => {
    if (required && (!formData || formData === '')) {
      setValidationError('ADO Product is required');
    } else {
      setValidationError(null);
    }
  }, [formData, required]);

  // Combine errors for display.
  const combinedError = validationError || fetchError;

  return (
    <FormControl fullWidth required={required} error={!!rawErrors?.length || !!combinedError}>
      <InputLabel>{schema.title}</InputLabel>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Select
          value={formData || ''}
          onChange={(e) => onChange(e.target.value as string)}
          disabled={!resolvedAdoProject} // Disable if no valid project is selected.
        >
          {options.map((option, idx) => (
            <MenuItem key={idx} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      )}
      {combinedError && <FormHelperText>{combinedError}</FormHelperText>}
    </FormControl>
  );
};